import React, { useState, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { loadStripe } from '@stripe/stripe-js';
import Select from 'react-select';
import { useAuth } from '../teacher/AuthContext';
import { Calendar, Clock, Trash2, CheckCircle, Lock, BookOpen, Award, GraduationCap, MapPin, FilterX } from 'lucide-react';

// --- CONFIGURATION MAPS BROUGHT IN FROM REGISTRATION ---
const COUNTRY_GRADE_SUBJECTS_MAP = {
  'United States': {
    'Pre-School Pre-K': ['Creative Arts', 'Literacy and Language Arts', 'Mathematics', 'Music and Movement', 'Practical Life/Self-care', 'Science', 'Social-Emotinal Development', 'Social Studies'],
    'Elementary School K1-K5': ['Fine Arts', 'ELA', 'Spanish', 'Library', 'Mathematics', 'Music', 'Physical Education', 'Religion', 'Science', 'Social Studies', 'Technology'],
    'Middle School K6-K8': ['English Language Arts', 'Mathematics','Science','Social Studies / History','Physical Education & Health','Electives'],
    'High School K9-K12': ['English Language Arts', 'Mathematics','Science','Social Studies / Humanities','World Languages','Physical Education & Health','Fine & Practical Arts / Career Technical Education (CTE)'],
    'Standard Tests': ['Accuplacer','ACT','ACT','AP','Bar Exam','CLT','DAT','GED','GMAT','GRE','IB','IELTS','ITBS','LSAT','MCAT','NCLEX','OAT','PCAT','PSAT','SAT','SAT10','TerraNova','TOEFL','USMLE'],
    'College and University': ['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  },
  'Canada': {
    'Pre-School & Kindergarten (Ages 3 to 5)': ['Language / Early Literacy', 'Mathematics (Number Sense)','Social and Emotional Development','Expressive Arts','Health and Physical Education','Science / Environmental Exploration'],
    'Elementary / Primary School (Grades 1 to 6 or 8)': ['Language / Early Literacy','French as a Second Language (FSL)','Mathematics','Science and Technology', 'Social Studies / History and Geography', 'Health and Physical Education','The Arts'],
    'Junior High / Middle School (Grades 7 to 9)': ['English / Language Arts', 'French / Second Language','Mathematics','Science (integrated general science covering biology, chemistry, and physics)','Social Studies / Canadian History / Geography','Health and Physical Education','Applied Design, Skills, and Technologies (ADST) / Technological Education','Arts (Music, Drama, Art)'],
    'High School / Secondary School (Grades 10 to 12)': ['English Language Arts', 'French as a Second Language','Mathematics','Science (Biology, Chemistry, Physics)','Canadian History / Canadian Geography','Health and Physical Education','Technological Education / Computer Studies','The Arts (Visual arts, Media, Music, Drama)', 'Civics and Career Studies','Indigenous-focused coursework','Business Studies (accounting, entrepreneurship, international business)','Social Sciences and Humanities (world history, psychology, law, philosophy)','Advanced Sciences & Specialized Math (Calculus, Earth sciences)','Co-operative Education and Apprenticeship/Trades programs'],
    'University / Post Secondary School': ['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  },
  'United Kingdom': {
    'Early Years Foundation Stage (EYFS: Pre-School & Reception, Ages 3 to 5)': ['Communication and Language','Physical Development','Personal, Social and Emotional Development','Literacy','Mathematics','Understanding the World','Expressive Arts and Design'],
    'Primary School (Key Stages 1 & 2, Ages 5 to 11 / Years 1 to 6)': ['English (reading, writing, grammar, vocabulary)','Mathematics (number, geometry, measurement, statistics)','Science (Biology, Chemistry, Physics foundations)','Arts and Design', 'Computing (coding, digital literacy, IT)','Design and Technology (DT)','Geography', 'History','Music','Physical Education (PE)', 'Modern Foreign Languages','Religious Education (RE)','Relationships and Health Education'],
    'Lower Secondary School (Key Stage 3, Ages 11 to 14 / Years 7 to 9)':['English','Mathematics','Science (Biology, Chemistry, Physics foundations)','Arts and Design', 'Computing','Design and Technology (DT)','Citizenship','Geography', 'History','Music','Physical Education (PE)', 'Modern Foreign Languages','Religious Education (RE)','Relationships and Sex Education (RSE)'],
    'Upper Secondary School (Key Stage 4 / GCSE, Ages 14 to 16 / Years 10 to 11)': ['English','Mathematics','Science (Biology, Chemistry, Physics foundations)','Arts and Design', 'Computing','Design and Technology (DT)','Citizenship','Geography', 'History','Music','Physical Education (PE)', 'Modern Foreign Languages','Religious Education (RE)','Relationships and Sex Education (RSE)'],
    'Post age 16 / Sixth Form / College (Key Stage 5, Ages 16 to 18)':['English','Mathematics','Science (Biology, Chemistry, Physics foundations)','Arts & Technology (e.g., Art and Design, Drama, Music, Design and Technology)','Humanities (e.g., History, Geography, Religious Studies)', 'Languages (e.g., French, Spanish, German)','Social Sciences and Practical subjects (e.g., Computer Science, Economics, Business Studies, Psychology, Sociology)'],
    'University (Higher Education, Age 18+)':['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  },
  'Australia': {
    'Foundation to Year 10': ['English (Language, Literature and Literacy)', 'Mathematics (number and algebra, measurement and geometry, and statistics and probability)', 'Science (biological, chemical, earth and environmental, and physical sciences)', 'The Arts (Dance, Drama, Media Arts, Music, and Visual Arts)', 'Technologies (Design and Technologies, and Digital Technologies)', 'Humanities and Social Sciences (HASS) (History, Geography, Civics and Citizenship, and Economics and Business)', 'Languages (Chinese, Japanese, French, Italian, Indonesian, and various Indigenous languages)', 'Health and Physical Education (HPE) (personal, social, and community health, movement, and physical activity)'],
    'Senior Secondary School (Years 11 to 12)':['English','Mathematics','Biology', 'Chemistry', 'Modern History','Physics', 'Business Studies', 'Arts and Design', 'Computing','Design and Technology (DT)','Citizenship','Geography', 'History','Music','Physical Education (PE)', 'Modern Foreign Languages','Religious Education (RE)','Relationships and Sex Education (RSE)'],
    'University Level':['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  },
  'South Africa': {
    'Early Childhood Development (Birth to Age 4 / Pre-School)': ['Well-being (Physical-motor development, health, nutrition, and safety)', 'Identity and Belonging (Self-awareness, self-care, and social relationships)', 'Communication (Listening, speaking, early literacy, and non-verbal expression)', 'Exploring Mathematics (Early number concepts, spatial awareness, and patterns)', 'Creativity (Art, music, imaginative play, and movement)', 'Knowledge and Understanding of the World (Natural environment, observation, and cultural awareness)'],
    'Foundation Phase (Grades R to 3 / Ages 5 to 9)': ['Home Language (Official language chosen as English, Afrikaans, IsiZulu)','First Additional Language (Second official language)','Mathematics (or Mathematics Grade R)','Life Skills (Creative Arts, Physical Education, and Personal/social well-being)'],
    'Intermediate Phase (Grades 4 to 6 / Ages 10 to 12)':['Home Language','First Additional Language','Mathematics','Natural Sciences and Technology (Combined science and basic tech concepts)','Social Sciences (History and Geography)','Life Skills (Creative Arts, Physical Education, Religion/Values)'],
    'Senior Phase (Grades 7 to 9 / Ages 13 to 15)': ['Home Language','First Additional Language','Mathematics','Natural Sciences (Biology, chemistry, physics, and earth science foundations)','Social Sciences (History and Geography)','Technology','Economic and Management Sciences (EMS) (Intro to accounting, business, and economics)','Life Orientation','Creative Arts','Coding and Robotics (In rolling/updated curricula)'],
    'Further Education and Training (FET Phase / Grades 10 to 12 / Matric)':['Home Language (Official language)','First Additional Language (Official language)','Mathematics or Mathematical Literacy','Life Orientation','Sciences & Mathematics (Physical Sciences, Life Sciences, Agricultural Sciences)','Commerce (Accounting, Business Studies, Economics)','Humanities & Social Sciences (History, Geography)','Technology & Computer Studies (Information Technology (IT), Computer Applications Technology (CAT))','Arts & Culture (Visual Arts, Dramatic Arts, Music)','Services & Practical (Consumer Studies, Tourism, Engineering Graphics and Design (EGD))'],
    'University Level (Higher Education / Undergraduate Degrees)':['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  },
  'Uganda': {
    'Kindergarten': ['Language and Literacy', 'Mathematical Concepts', 'Environmental Interaction: Science/Social Studies', 'Personal and Social Development','Physical Development: Health/Motor skills','Creative and Expressive Arts'],
    'Primary': ['English', 'Mathematics', 'Science', 'Social Studies'],
    'Secondary: O Level': ['Agriculture', 'Arabic', 'Biology', 'Chemistry', 'Chinese', 'Christian Religious Education', 'English Language', 'Entrepreneurship', 'Fine Arts', 'French', 'General Science', 'German', 'History and Political Education', 'Information Communication and Technology', 'Islamic Religious Education', 'Kiswahili', 'Latin', 'Literature in English', 'Luganda', 'Lusoga', 'Mathematics', 'Nutrition and Food Technology', 'Physical Education', 'Performing Arts', 'Physics', 'Technology and Design'],
    'Secondary: A Level': ['Agriculture', 'Arabic', 'Biology', 'Chemistry', 'Chinese', 'Christian Religious Education', 'English Language', 'Entrepreneurship', 'Fine Arts', 'French', 'General Science', 'German', 'History and Political Education', 'Information Communication and Technology', 'Islamic Religious Education', 'Kiswahili', 'Latin', 'Literature in English', 'Luganda', 'Lusoga', 'Mathematics', 'Nutrition and Food Technology', 'Physical Education', 'Performing Arts', 'Physics', 'Technology and Design'],
    'Mature Entry': ['English Language', 'Mathematics', 'Aptitude test/Specialized Paper', 'General Paper'],
    'University': ['Algebra', 'Business Administration', 'Calculus', 'Computer Science', 'Economics', 'Engineering', 'Nursing', 'Pharmacy', 'Psychology', 'Medicine', 'Statistics', 'Agriculture', 'Geography', 'Public Policy'],
  }
};

const SEN_DATA_MAP = {
  'Neurodiversity & Learning Differences': [
    'Dyslexia Support', 
    'Dyscalculia Strategies', 
    'ADHD Focus & Executive Functioning', 
    'Autism Spectrum (ASD) Accommodations', 
    'Auditory / Visual Processing Delays'
  ],
  'Speech, Language & Communication': [
    'Speech Sound Disorders', 
    'Expressive Language Support', 
    'Receptive Language Difficulties', 
    'Stuttering & Fluency Support'
  ],
  'Physical & Sensory Accommodations': [
    'Visual Impairment / Braille / High-Contrast Support', 
    'Hearing Impairment / ASL / FM Systems', 
    'Fine/Gross Motor Skills Adjustments', 
    'Sensory Processing Integration'
  ],
  'Emotional & Behavioral Support': [
    'Anxiety Management & Safe Spaces', 
    'Behavioral Intervention Plans (BIP)', 
    'Social Skills Training'
  ]
};

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
if (!stripeKey) {
  console.error("VITE_STRIPE_PUBLISHABLE_KEY is missing! 1. Check .env is in root. 2. Restart Dev Server.");
}

const formatPrice = (amount, currencyCode) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 0, 
    }).format(amount);
  } catch (e) {
    return `${currencyCode || '$'} ${amount}`;
  }
};

const Tutors_23 = () => {
  const { currentUser, logout, loading, isTutor, isAdmin } = useAuth();
  const pendingApproval = isTutor && currentUser && !currentUser.approved;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 pt-24 text-center">
        <div className="space-y-4">
          <p className="text-xl font-semibold text-gray-700">Checking Dashboard Permissions...</p>
          <div className="border-t-2 border-b-2 border-blue-600 rounded-full w-10 h-10 animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!currentUser || (!isTutor && !isAdmin)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 pt-24 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-red-200">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 max-w-sm">This dashboard is reserved for verified Tutors or Administrative personnel. If you believe this is an error, please contact support.</p>
          {currentUser && <button onClick={logout} className="mt-6 text-sm text-blue-600 font-semibold hover:underline">Log Out and Try Again</button>}
        </div>
      </div>
    );
  }

  if (pendingApproval) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 pt-24 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-purple-200">
          <Clock className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-gray-900 mb-2">Account Pending Approval</h1>
          <p className="text-gray-600 max-w-sm">Welcome! Your tutor registration is currently being reviewed by our administrative team. You will have full access once approved.</p>
        </div>
      </div>
    );
  }
   
  const [tutors, setTutors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [studentEmail, setStudentEmail] = useState(""); 
  const [selectedTutorId, setSelectedTutorId] = useState("");
  const [viewDate, setViewDate] = useState(DateTime.now().startOf('week'));
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [existingBookings, setExistingBookings] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);

  // --- FILTER STATES USING 'All' ---
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterGrade, setFilterGrade] = useState('All');
  const [filterSubject, setFilterSubject] = useState('All');
  const [filterName, setFilterName] = useState('All');
  const [maxPrice, setMaxPrice] = useState(50000);

  // --- SEN FILTER STATES USING 'All' ---
  const [filterSenArea, setFilterSenArea] = useState('All');
  const [filterSenType, setFilterSenType] = useState('All');

  const role = isTutor ? 'tutor' : (isAdmin ? 'administrator' : (currentUser?.activeRole || 'learner'));

  // --- CASCADING RESET EFFECTS ---
  useEffect(() => {
    setFilterGrade('All');
    setFilterSubject('All');
  }, [filterCountry]);

  useEffect(() => {
    setFilterSubject('All');
  }, [filterGrade]);

  useEffect(() => {
    setFilterSenType('All');
  }, [filterSenArea]);

  // --- DYNAMIC FILTER LIST CALCULATIONS ---
  const availableCountries = useMemo(() => {
    return ['All', ...new Set(tutors.map(t => t.country).filter(Boolean))];
  }, [tutors]);

  const availableGrades = useMemo(() => {
    if (filterCountry === 'All') {
      return ['All', ...new Set(tutors.map(t => t.grade_levels).filter(Boolean))];
    }
    return ['All', ...Object.keys(COUNTRY_GRADE_SUBJECTS_MAP[filterCountry] || {})];
  }, [tutors, filterCountry]);

  const availableSubjects = useMemo(() => {
    if (filterCountry === 'All' || filterGrade === 'All') {
      const fallback = tutors.flatMap(t => [t.subject1, t.subject2]).filter(Boolean);
      return ['All', ...new Set(fallback)];
    }
    return ['All', ...(COUNTRY_GRADE_SUBJECTS_MAP[filterCountry]?.[filterGrade] || [])];
  }, [tutors, filterCountry, filterGrade]);

  const senAreaOptions = useMemo(() => {
    return ['All', ...Object.keys(SEN_DATA_MAP)];
  }, []);

  const availableSenTypes = useMemo(() => {
    if (filterSenArea === 'All') return [];
    return ['All', ...(SEN_DATA_MAP[filterSenArea] || [])];
  }, [filterSenArea]);

  const uniqueNames = useMemo(() => {
    return ['All', ...new Set(tutors.map(t => `${t.first_name} ${t.last_name}`).filter(Boolean))];
  }, [tutors]);

  useEffect(() => {
    if (currentUser?.email && !studentEmail) {
      setStudentEmail(currentUser.email);
      if (role === 'tutor' && currentUser?.id) setSelectedTutorId(currentUser.id.toString());
    }
  }, [currentUser, role]);

  useEffect(() => {
    if (!currentUser || pendingApproval) return;
    setIsLoading(true);
    fetch('/api/tutor')
      .then(res => res.json())
      .then(data => {
        const tutorList = Array.isArray(data) ? data : (data.rows || []);
        setTutors(tutorList);
        if (tutorList.length > 0 && role !== 'tutor' && role !== 'administrator') {        
          setSelectedTutorId(tutorList[0].id.toString());
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Fetch error:", err);
        setIsLoading(false);
      });
  }, [currentUser, role]);

  // --- COMPLEX DATA GRID FILTERING ---
  const filteredTutors = useMemo(() => {
    return tutors.filter(t => {
      const matchesCountry = filterCountry === 'All' || t.country === filterCountry;
      const matchesGrade = filterGrade === 'All' || t.grade_levels === filterGrade;
      const matchesSubject = filterSubject === 'All' || t.subject1 === filterSubject || t.subject2 === filterSubject;
      const matchesPrice = parseFloat(t.hourly_rate || 0) <= maxPrice;
      const matchesName = filterName === 'All' || `${t.first_name} ${t.last_name}` === filterName;
      
      let matchesSen = true;
      if (filterSenArea !== 'All') {
        try {
          const parsedSen = typeof t.sen_area === 'string' ? JSON.parse(t.sen_area) : (t.sen_area || []);
          matchesSen = Array.isArray(parsedSen) && parsedSen.some(item => {
            const matchesCat = item.area === filterSenArea;
            const matchesType = filterSenType === 'All' || item.need === filterSenType;
            return matchesCat && matchesType;
          });
        } catch (e) {
          matchesSen = false; 
        }
      }
      return matchesCountry && matchesGrade && matchesSubject && matchesPrice && matchesName && matchesSen;
    });
  }, [tutors, filterCountry, filterGrade, filterSubject, filterSenArea, filterSenType, filterName, maxPrice]);

  const tutorOptions = useMemo(() => filteredTutors.map(t => ({
    value: t.id.toString(),
    label: `${t.first_name} ${t.last_name} - ${formatPrice(t.hourly_rate, t.currency)} (${t.subject1})`
  })), [filteredTutors]);

  const selectedTutorData = useMemo(() => 
    tutors.find(t => t.id.toString() === selectedTutorId), [tutors, selectedTutorId]);

  useEffect(() => {  
    if (!selectedTutorId || pendingApproval) return;
    const fetchExistingSlots = async () => {
      try {
        const endpoint = role === 'tutor' ? `/api/availability/${selectedTutorId}` : `/api/buks/${selectedTutorId}`;
        const res = await fetch(endpoint);     
        const data = await res.json();  
      
        if (role === 'tutor') {
          setSelectedSlots(new Set(data.map(s => s.iso_string)));
        } else {
          setExistingBookings(Array.isArray(data) ? data : []);
        }
      } catch(err) {
        console.error("Fetch bookings error:", err);
      }
    };
    fetchExistingSlots();
  }, [selectedTutorId, role]);   

  const weekDays = useMemo(() => {
    return [...Array(7)].map((_, i) => viewDate.plus({ days: i }));
  }, [viewDate]);  

  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const resetFilters = () => {
    setFilterCountry('All');
    setFilterGrade('All');
    setFilterSubject('All');
    setFilterSenArea('All');
    setFilterSenType('All');
    setFilterName('All');
    setMaxPrice(50000);
  };
 
  const handleSaveAvailability = async () => {
    setActionLoading(true);
    try {
      const response = await fetch('/api/tutor/availability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tutor_id: currentUser.id,
          slots: Array.from(selectedSlots)
        })
      });
      if (response.ok) {
        alert("Availability updated successfully!");
        setIsDirty(false);
      }
    } catch (err) {
      alert("Failed to save Availability.");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setDragMode(null);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const updateSlots = (isoStr, mode) => {  
    const newSlots = new Set(selectedSlots);
    mode === 'select' ? newSlots.add(isoStr) : newSlots.delete(isoStr);
    setSelectedSlots(newSlots);
    setIsDirty(true);
  };

  const handleMouseDown = (isoStr) => {
    if (DateTime.fromISO(isoStr) < DateTime.utc() || existingBookings.includes(isoStr)) return;        
    setIsDragging(true);
    const mode = selectedSlots.has(isoStr) ? 'deselect' : 'select';
    setDragMode(mode);
    updateSlots(isoStr, mode);
  };   

  const handleMouseEnter = (isoStr) => {
    if (!isDragging || DateTime.fromISO(isoStr) < DateTime.utc() || existingBookings.includes(isoStr)) return;
    updateSlots(isoStr, dragMode);
  };

  const getSlotISO = (day, hour) => day.set({ hour }).startOf('hour').toUTC().toISO(); 

  const toggleColumn = (hour, checked) => {
    const newSlots = new Set(selectedSlots);
    weekDays.forEach(day => {
      const slot = getSlotISO(day, hour);
      if (DateTime.fromISO(slot) >= DateTime.utc() && !existingBookings.includes(slot)) {
        checked ? newSlots.add(slot) : newSlots.delete(slot);
      }
    });
    setSelectedSlots(newSlots);
    setIsDirty(true);
  };

  const toggleRow = (day, checked) => {
    const newSlots = new Set(selectedSlots);
    for (let hour = 0; hour < 24; hour++) {
      const slot = getSlotISO(day, hour);
      if (DateTime.fromISO(slot) >= DateTime.utc() && !existingBookings.includes(slot)) {
        checked ? newSlots.add(slot) : newSlots.delete(slot);
      }
    }
    setSelectedSlots(newSlots);
    setIsDirty(true);
  };

  const toggleAllMatrix = (checked) => {
    const newSlots = new Set(selectedSlots);
    weekDays.forEach(day => {
      for (let hour = 0; hour < 24; hour++) {
        const slot = getSlotISO(day, hour);      
        if (DateTime.fromISO(slot) >= DateTime.utc() && !existingBookings.includes(slot)) {
          checked ? newSlots.add(slot) : newSlots.delete(slot);
        }
      }
    });
    setSelectedSlots(newSlots);
    setIsDirty(true);
  };
    
  const handleApply = async () => {
    if (!studentEmail) return alert("Please enter your email address first.");        
    if (selectedSlots.size === 0) return alert("Please select at least one time slot."); 
    if (!stripeKey) {
      return alert("Missing VITE_STRIPE_PUBLISHABLE_KEY in .env file. Please restart your dev server.");
    }
    if (!stripePromise) {
      return alert("Stripe failed to initialize. Check your API key format.");
    }
    setActionLoading(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load. Check your API key.");
      const response = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tutor_id: parseInt(selectedTutorId),
          slots: Array.from(selectedSlots),
          tutorTimezone: DateTime.local().zoneName,
          student_email: studentEmail
        })
      });

      const data = await response.json();
      if (!response.ok) { 
        throw new Error(data.message || data.error || "Server failed to create session");
      }          
      if (data.url) {
        setIsDirty(false);
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received from server.");
      }
    } catch (err) {
      console.error("Post error:", err);
      alert(`Payment integration failure: ${err.message}`);
    } finally { 
      setActionLoading(false);
    } 
  };

  const getAvailableSlots = (type, value) => {
    let slots = [];
    if (type === 'row') {
      for (let hour = 0; hour < 24; hour++) {
        slots.push(getSlotISO(value, hour));
      }
    } else if (type === 'col') {
      weekDays.forEach(day => slots.push(getSlotISO(day, value)));
    } else if (type === 'matrix') {
      weekDays.forEach(day => {
        for (let hour = 0; hour < 24; hour++) {
          slots.push(getSlotISO(day, hour));
        }
      });
    }
    return slots.filter(slot => 
      DateTime.fromISO(slot) >= DateTime.utc() && !existingBookings.includes(slot)
    );
  };

  const isGroupSelected = (slots) => {
    if (slots.length === 0) return false;
    return slots.every(slot => selectedSlots.has(slot));
  };
    
  return (
    <div className="pt-24 shadow-md sm:rounded-lg bg-white min-h-screen p-6">
      {/* Dynamic Workspace Banner */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 shadow-sm gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Active User: <span className="text-blue-600 font-semibold font-mono">{currentUser.email}</span></h1>
          <div className="mt-2 flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${role === 'tutor' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
              Access Context: {role}
            </span>
            {pendingApproval && <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-orange-100 text-orange-700">Awaiting Approval</span>}
          </div>
        </div>
        <button onClick={logout} className="text-sm font-semibold text-red-600 hover:underline">Logout</button>
      </div>

      {!pendingApproval ? (
        <>
          {role !== 'tutor' && (
            <div className="mb-8 p-6 bg-gray-50/50 rounded-2xl border border-gray-200/80 backdrop-blur-sm space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-gray-200 gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-gray-900">Search TRiANf Tutors</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Filter by system parameters to render profile summary listings</p>
                </div>
                <button onClick={resetFilters} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 text-xs font-bold text-gray-600 hover:text-blue-600 hover:border-blue-300 rounded-xl transition-all shadow-sm">
                  <FilterX size={14} /> Clear Active Filters
                </button>
              </div>                 

              {/* --- ACADEMIC & LOCATION FILTERS --- */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Country Control */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Country</label>
                  <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all">
                    {availableCountries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Grade Tier Control */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Grade Level</label>
                  <select value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)} className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all">
                    {availableGrades.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>

                {/* Subject Field Control */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Search Subject</label>
                  <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all">
                    {availableSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Name Selector */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Search Tutor Name</label>
                  <select value={filterName} onChange={(e) => setFilterName(e.target.value)} className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all">
                    {uniqueNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {/* --- SPECIAL EDUCATIONAL NEEDS (SEN) FILTER SECTION UI --- */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                  Special Educational Needs (SEN) Filtering
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* SEN Category Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">SEN Category</label>
                    <select
                      className="w-full border border-gray-200 rounded-xl p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      value={filterSenArea}
                      onChange={(e) => setFilterSenArea(e.target.value)}
                    >
                      {senAreaOptions.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dependent SEN Types Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Specific Need / Support Type</label>
                    <select
                      className="w-full border border-gray-200 rounded-xl p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                      value={filterSenType}
                      onChange={(e) => setFilterSenType(e.target.value)}
                      disabled={filterSenArea === 'All'}
                    >
                      <option value="All">
                        {filterSenArea === 'All' ? 'Select a category first...' : 'All Specific Types...'}
                      </option>
                      {availableSenTypes.filter(t => t !== 'All').map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 items-center">
                {/* Price Sliding Range */}
                <div className="bg-white p-4 rounded-xl border border-gray-200/70 shadow-sm">
                  <div className="flex justify-between text-xs text-gray-700 mb-2 font-bold">
                    <span>Max Hourly Compensation:</span>
                    <span className="text-blue-600 font-mono">${maxPrice}</span>
                  </div>
                  <input type="range" min="0" max="50000" step="10" value={maxPrice} onChange={(e) => setMaxPrice(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />  
                  <div className="flex justify-between text-[10px] text-gray-400 font-semibold mt-1">
                    <span>$0</span>
                    <span>$50,000</span>
                  </div>
                </div>

                {/* Master Select Menu */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Confirmed Booking Target</label>
                  <Select options={tutorOptions} isLoading={isLoading} onChange={(opt) => setSelectedTutorId(opt ? opt.value : "")} placeholder="Select a Tutor..." value={tutorOptions.find(o => o.value === selectedTutorId)} className="text-sm" styles={{ control: (base) => ({ ...base, borderRadius: '12px', padding: '3px', borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }) }} />
                </div>

                {/* Student Validation Identity Field */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase font-extrabold tracking-wider text-gray-400">Student Delivery Email</label>
                  <input type="email" className="w-full p-2.5 border border-gray-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500" placeholder="email@example.com" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} required />
                </div>
              </div>

              {/* Tutor Profile Grid */}
              <div className="mt-8">
                <h3 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen size={18} className="text-blue-500" />
                  Available Dynamic Match Cards ({filteredTutors.length})
                </h3>
                
                {filteredTutors.length === 0 ? (
                  <div className="p-8 text-center bg-white border border-dashed rounded-2xl text-gray-400 font-medium text-sm">
                    No verified tutor matching profiles fit the parameters currently specified.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTutors.map((t) => {
                      const isTargetSelected = selectedTutorId === t.id.toString();
                      let parsedSenArea = [];
                      try {
                        parsedSenArea = typeof t.sen_area === 'string' ? JSON.parse(t.sen_area) : (t.sen_area || []);
                      } catch(e) {}

                      return (
                        <div 
                          key={t.id} 
                          onClick={() => setSelectedTutorId(t.id.toString())}
                          className={`cursor-pointer rounded-2xl border-2 p-5 bg-white transition-all duration-300 relative flex flex-col justify-between shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                            isTargetSelected 
                              ? 'border-blue-600 bg-blue-50/10 ring-4 ring-blue-50' 
                              : 'border-gray-200/80 hover:border-blue-300'
                          }`}
                        >
                          {isTargetSelected && (
                            <span className="absolute top-3 right-3 bg-blue-600 text-white p-1 rounded-full">
                              <CheckCircle size={14} />
                            </span>
                          )}
                          
                          <div>
                            <div className="flex items-center gap-4 mb-4">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-black text-base shadow-sm">
                                {t.first_name?.[0]}{t.last_name?.[0]}
                              </div>
                              <div>
                                <h4 className="font-extrabold text-gray-900 text-base">{t.first_name} {t.last_name}</h4>
                                <p className="text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-md inline-block border border-emerald-100 mt-0.5">
                                  {formatPrice(t.hourly_rate, t.currency)}/hr
                                </p>
                              </div>
                            </div>

                            <p className="text-xs text-gray-600 line-clamp-3 italic mb-4">
                              "{t.bio || 'Academic Educator profile pending verification bio.'}"
                            </p>

                            <div className="space-y-2 border-t pt-3 border-gray-100">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <MapPin size={14} className="text-gray-400 shrink-0" />
                                <span className="font-medium truncate">{t.country || 'Global Infrastructure'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <GraduationCap size={14} className="text-gray-400 shrink-0" />
                                <span className="font-medium truncate">{t.highest_edn || 'Standard Tier Tiering'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <Award size={14} className="text-gray-400 shrink-0" />
                                <span className="font-semibold text-blue-600 truncate">{t.grade_levels} • {t.subject1}</span>
                              </div>
                            </div>
                          </div>

                          {parsedSenArea.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
                              {parsedSenArea.slice(0, 2).map((item, idx) => (
                                <span key={idx} className="text-[10px] bg-purple-50 text-purple-700 border border-purple-100 font-bold px-2 py-0.5 rounded-md truncate max-w-full">
                                  🧬 {item.need || item.area}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Single Tutor Card Overview Summary Panel */}
          {selectedTutorData && role !== 'tutor' && (
            <div className="mb-8 p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-3xl shadow-xl flex flex-col md:flex-row gap-6 items-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white font-black text-3xl border-2 border-white/30 shadow-inner shrink-0">
                {selectedTutorData.first_name?.[0]}{selectedTutorData.last_name?.[0]}
              </div>
              <div className="flex-1 w-full text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                  <h2 className="text-2xl font-black tracking-tight">{selectedTutorData.first_name} {selectedTutorData.last_name}</h2>
                  <span className="text-xl font-mono font-black bg-white text-blue-700 px-4 py-1 rounded-2xl shadow-md inline-block">
                    {formatPrice(selectedTutorData.hourly_rate, selectedTutorData.currency)}/hr
                  </span>
                </div>
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3 text-xs">
                  <span className="bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg font-bold border border-white/10">{selectedTutorData.grade_levels || 'All Grades'}</span>
                  <span className="bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg font-bold border border-white/10">Major: {selectedTutorData.subject1}</span>
                  {selectedTutorData.subject2 && <span className="bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg font-bold border border-white/10">Minor: {selectedTutorData.subject2}</span>}
                  <span className="bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg font-medium border border-white/10">{selectedTutorData.highest_edn}</span>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed max-w-3xl italic">"{selectedTutorData.bio || 'Dynamic tutoring profile text currently configuration pending.'}"</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white rounded shadow" onClick={() => setViewDate(viewDate.minus({ weeks: 1 }))}>← Previous Week</button>
            <div className="text-center min-w-[200px]">
              <div className="text-sm font-bold text-gray-500 uppercase">Viewing Week</div>
              <div className="font-bold">{weekDays[0].toFormat('MMM dd')} - {weekDays[6].toFormat('MMM dd, yyyy')}</div>
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white rounded shadow" onClick={() => setViewDate(DateTime.now().startOf('week'))}>This Week</button>
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white rounded shadow" onClick={() => setViewDate(viewDate.plus({ weeks: 1 }))}>Next Week →</button>
          </div>
          
          {/* Calendar Grid */}
          <div className="border rounded border-gray-200 overflow-x-scroll whitespace-nowrap scroll-smooth flex scrollbar-hide mt-4">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border-b border-r sticky left-0 bg-gray-100 z-10 min-w-[120px]">
                    Day / Time (Local)
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] uppercase font-bold text-gray-500">Select All</span>
                      <input type="checkbox" title="Select all checkboxes" checked={isGroupSelected(getAvailableSlots('matrix'))} onChange={(e) => toggleAllMatrix(e.target.checked)} />
                    </div>
                  </th>            
                  {timeSlots.map(hour => {
                    const isCurrentHourColumn = DateTime.now().hour === hour; 
                    return (
                      <th key={hour} className={`p-2 border-b border-r text-center min-w-20 ${isCurrentHourColumn ? 'text-blue-600 bg-blue-50' : ''}`}>
                        <div className={`text-xs font-semibold ${isCurrentHourColumn ? 'scale-110' : ''}`}>{hour}:00</div>
                        <div className="text-[10px] text-gray-500">Tutor:{hour}:00</div> 
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-semibold text-gray-500">All</span>                              
                          <input type="checkbox" title="Select all days" checked={isGroupSelected(getAvailableSlots('col', hour))} onChange={(e) => toggleColumn(hour, e.target.checked)} />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody onMouseLeave={() => setIsDragging(false)}>                    
                {weekDays.map(day => {
                  const isToday = day.hasSame(DateTime.now(), 'day'); 
                  const currentHour = DateTime.now().hour;
                  const isDayPast = day.endOf('day') < DateTime.now();
                  return (
                    <tr key={day.toISODate()} className={isToday ? 'bg-blue-50/20' : ''}>
                      <td className={`sticky left-0 bg-white p-2 border-b border-r font-medium z-10 ${
                        isToday ? 'bg-blue-100 text-blue-700 border-l-4 border-l-blue-600' : isDayPast ? 'bg-gray-200 text-gray-400' : 'bg-white'
                      }`}>
                        <div className="flex items-center justify-between px-1">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase font-bold opacity-60">{isToday ? 'Today' : day.toFormat('ccc')}</span> 
                            <span className="text-xs">{day.toFormat('dd LLL')}</span>
                          </div>
                          <span className="text-[10px] font-semibold text-gray-500">All</span> 
                          <input type="checkbox" className="ml-2" title="Select entire day" checked={isGroupSelected(getAvailableSlots('row', day))} onChange={(e) => toggleRow(day, e.target.checked)} />
                        </div>
                      </td>
                      {timeSlots.map(hour => {
                        const slot = getSlotISO(day, hour);
                        const isBooked = existingBookings.includes(slot);
                        const isSelected = selectedSlots.has(slot);
                        const isCurrentHour = isToday && hour === currentHour;
                        const isPast = DateTime.fromISO(slot) < DateTime.now();

                        return (
                          <td 
                            key={hour} 
                            onMouseDown={() => handleMouseDown(slot)}
                            onMouseEnter={() => handleMouseEnter(slot)}
                            className={`p-3 border-b border-r text-center cursor-crosshair transition-all relative ${
                              isPast ? 'bg-gray-100 cursor-not-allowed' : isBooked ? 'bg-red-50' : isSelected ? 'bg-green-400' : isCurrentHour ? 'bg-blue-100/50 cursor-crosshair' : 'cursor-crosshair'
                            }`}
                          >
                            {isCurrentHour && (
                              <div className="absolute inset-y-0 left-0 w-1 bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] z-10" title="Current Hour" />
                            )}
                            {isBooked ? (
                              <span title="Already booked">❌</span>
                            ) : isPast ? (
                              <span className="opacity-20">🚫</span>
                            ) : (
                              <input type="checkbox" checked={isSelected} readOnly className="pointer-events-none" />
                            )}                                          
                          </td>
                        );
                      })}
                    </tr>                        
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer Controls */}
          <div className="mt-8 p-6 bg-[#fbc901] rounded-xl border border-gray-200 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-gray-500 text-sm">Slots Selected:  
              <span className="ml-2 font-bold text-blue-600 text-lg">{selectedSlots.size}</span>
            </div>
            <div className="flex gap-4">
              <button onClick={() => { setSelectedSlots(new Set()); setIsDirty(true); }} className="bg-red-600 text-white text-sm font-semibold px-3 py-1 rounded hover:bg-red-500 flex items-center gap-1">
                <Trash2 className="w-4 h-4" />Clear All Selections
              </button>
              {role === 'tutor' ? (  
                <button 
                  onClick={handleSaveAvailability}
                  disabled={actionLoading}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {actionLoading ? "Saving..." : "Save My Availability"}
                </button>
              ) : (      
                <button 
                  className="w-full md:w-auto bg-blue-600 text-white px-10 py-3 rounded-lg font-bold shadow-lg transition-transform active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed" 
                  disabled={selectedSlots.size === 0 || actionLoading} 
                  onClick={handleApply} 
                >
                  {actionLoading ? "Processing..." : "Apply & Pay"}
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed text-gray-500 font-medium text-sm">
          Access to availability editing will be enabled once your account is verified.
        </div>
      )}
    </div>
  );
};

export default Tutors_23;